import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, message, Tabs, Table, Modal, Input, InputNumber, Select, Tag, Space, Popconfirm, Empty, Statistic, Row, Col, Upload,
} from 'antd';
import { PlusOutlined, ReloadOutlined, FileTextOutlined, CalculatorOutlined, BankOutlined, CloudUploadOutlined, PaperClipOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch, notifyDataChanged, apiUpload, apiUploadMany } from '../utils/api.js';

const INK = '#26201C';
const CORAL = '#D85A30';
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtYuan = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '¥0';
  const neg = n < 0;
  const abs = Math.abs(n);
  const s = abs >= 10000 ? (abs / 10000).toFixed(2) + '万' : abs.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
  return (neg ? '-¥' : '¥') + s;
};

export default function TaxCenter() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sale');

  // 销项发票
  const [invoices, setInvoices] = useState([]);
  const [saleSum, setSaleSum] = useState(null);
  const [entities, setEntities] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saleTypes, setSaleTypes] = useState([]);
  const [openSale, setOpenSale] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [recognizing, setRecognizing] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [sale, setSale] = useState({ customer_name: '', customer_id: '', tax_no: '', order_nos: '', invoice_no: '', invoice_type: '普票', amount_total: '', tax: '', entity_id: '', invoice_date: '', status: '已开', remark: '' });

  // 税负测算
  const [taxMonth, setTaxMonth] = useState(dayjs().format('YYYY-MM'));
  const [taxData, setTaxData] = useState(null);

  const loadSale = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([
        apiFetch('/api/sale-invoice/meta').then(x => x.json()),
        apiFetch('/api/sale-invoice').then(x => x.json()),
      ]);
      if (m.success) { setEntities(m.entities || []); setSaleTypes(m.types || []); setCustomers(m.customers || []); }
      if (l.success) setInvoices(l.data || []);
      else message.error(l.error || '销项发票加载失败');
    } catch (e) { message.error('销项发票加载失败：' + (e.message || '')); }
    setLoading(false);
  }, []);

  const loadTax = useCallback(async (m) => {
    try {
      const r = await apiFetch('/api/tax-calc?month=' + m).then(x => x.json());
      if (r.success) setTaxData(r.data || null);
      else message.error(r.error || '税负测算失败');
    } catch (e) { message.error('税负测算失败：' + e.message); }
  }, []);

  useEffect(() => { loadSale(); }, [loadSale]);
  useEffect(() => { loadTax(taxMonth); }, [taxMonth, loadTax]);

  const submitSale = async () => {
    if (!sale.customer_name) return message.warning('请填写客户名称');
    if (!sale.amount_total || Number(sale.amount_total) <= 0) return message.warning('请填写正数的价税合计');
    try {
      const body = {
        ...sale,
        amount_total: Number(sale.amount_total), tax: Number(sale.tax || 0),
        entity_id: sale.entity_id === '' ? null : Number(sale.entity_id),
        customer_id: sale.customer_id === '' || sale.customer_id == null ? null : Number(sale.customer_id),
      };
      if (editSale) body.id = editSale.id;
      const url = editSale ? '/api/sale-invoice/' + editSale.id : '/api/sale-invoice';
      const method = editSale ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(x => x.json());
      if (res.success) { setOpenSale(false); setEditSale(null); message.success(editSale ? '销项发票已保存' : '销项发票已登记'); notifyDataChanged(); loadSale(); loadTax(taxMonth); }
      else message.error(res.error || '保存失败');
    } catch (e) { message.error('保存失败：' + e.message); }
  };
  const recognizeInvoice = async (file) => {
    setRecognizing(true);
    try {
      const j = await apiUpload('/api/sale-invoice/recognize', file);
      setRecognizing(false);
      if (j && j.success && j.data) {
        const d = j.data;
        setSale(prev => ({
          ...prev,
          customer_name: d.customer_name || prev.customer_name,
          tax_no: d.customer_tax_no || prev.tax_no,
          invoice_no: d.invoice_no || prev.invoice_no,
          invoice_type: (d.invoice_type && ['专票', '普票', '电子普票'].includes(d.invoice_type)) ? d.invoice_type : prev.invoice_type,
          amount_total: d.amount_total != null ? d.amount_total : prev.amount_total,
          tax: d.tax != null ? d.tax : prev.tax,
          invoice_date: d.invoice_date || prev.invoice_date,
        }));
        let note = '识别成功，请核对后保存';
        if (d.tax_rate) note += '（税率 ' + d.tax_rate + '）';
        if (d.recon_warning) { message.warning(d.recon_warning); } else { message.success(note); }
      } else {
        message.error((j && j.error) || '识别失败');
      }
    } catch (e) {
      setRecognizing(false);
      message.error('识别失败：' + (e.message || ''));
    }
  };
  // 上传 → 识别 → 自动入库（一步到位）；识别不完整则回退表单让用户补全
  const recognizeAndSave = async (file) => {
    setRecognizing(true);
    try {
      const j = await apiUpload('/api/sale-invoice/recognize-save', file);
      setRecognizing(false);
      if (j && j.success) {
        const d = j.data || {};
        let msg = '已识别并保存：' + (d.customer_name || '') + '  ¥' + (Number(d.amount_total) || 0).toFixed(2);
        if (j.matched && j.matched.entity_name) msg += '（主体：' + j.matched.entity_name + '）';
        if (j.recon_warning) message.warning(msg + '；' + j.recon_warning);
        else message.success(msg);
        loadSale(); loadTax(taxMonth); notifyDataChanged();
      } else {
        const d = j && j.data;
        const hasPartial = d && (d.customer_name || d.amount_total != null || d.invoice_no || d.invoice_date);
        if (hasPartial) {
          setEditSale(null);
          setSale({
            customer_name: d.customer_name || '', customer_id: '', tax_no: d.customer_tax_no || '',
            order_nos: '', invoice_no: d.invoice_no || '',
            invoice_type: (d.invoice_type && ['专票', '普票', '电子普票'].includes(d.invoice_type)) ? d.invoice_type : '普票',
            amount_total: d.amount_total != null ? d.amount_total : '', tax: d.tax != null ? d.tax : '',
            entity_id: '', invoice_date: d.invoice_date || dayjs().format('YYYY-MM-DD'), status: '已开', remark: '',
          });
          setOpenSale(true);
          message.warning((j && j.error) || '识别不完整，已回填表单，请补全后保存');
        } else {
          message.error((j && j.error) || '识别失败');
        }
      }
    } catch (e) {
      setRecognizing(false);
      message.error('识别失败：' + (e.message || ''));
    }
  };
  // 批量上传：一次传多张 → 逐张识别 → 自动入库，返回成功/失败明细
  const recognizeBatch = async (files) => {
    if (!files || !files.length) return;
    setBatchLoading(true);
    setBatchResult(null);
    try {
      const j = await apiUploadMany('/api/sale-invoice/recognize-save-batch', files);
      setBatchLoading(false);
      if (j && j.success) {
        setBatchResult(j);
        setBatchOpen(true);
        loadSale(); loadTax(taxMonth); notifyDataChanged();
      } else {
        message.error((j && j.error) || '批量识别失败');
      }
    } catch (e) {
      setBatchLoading(false);
      message.error('批量识别失败：' + (e.message || ''));
    }
  };
  const delSale = async (id) => {
    try {
      const res = await apiFetch('/api/sale-invoice/' + id, { method: 'DELETE' }).then(x => x.json());
      if (res.success) { message.success('已删除'); notifyDataChanged(); loadSale(); loadTax(taxMonth); }
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };
  const delFile = async (id) => {
    try {
      const res = await apiFetch('/api/sale-invoice/' + id + '/file', { method: 'DELETE' }).then(x => x.json());
      if (res.success) { message.success('已删除发票文件'); loadSale(); }
      else message.error(res.error || '删除失败');
    } catch (e) { message.error('删除失败：' + e.message); }
  };

  const setTaxpayerType = async (entityId, taxpayerType) => {
    try {
      const e = entities.find(x => Number(x.id) === Number(entityId));
      if (!e) return;
      const res = await apiFetch('/api/ledger/entity', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: entityId, name: e.name, type: e.type, tax_no: e.tax_no || '', taxpayer_type: taxpayerType, remark: e.remark || '' }),
      }).then(x => x.json());
      if (res.success) { message.success('已更新纳税人身份'); loadSale(); loadTax(taxMonth); }
      else message.error(res.error || '更新失败');
    } catch (err) { message.error('更新失败：' + err.message); }
  };

  // ===== 销项发票 Tab =====
  const saleColumns = [
    { title: '客户（抬头）', dataIndex: 'customer_name', width: 160, render: v => <b>{v}</b> },
    { title: '关联客户档案', dataIndex: 'customer_profile_name', width: 140, render: v => v ? <Tag color="blue" style={{ borderRadius: 999 }}>{v}</Tag> : <span style={{ color: '#C0B8B0', fontSize: 12 }}>未关联</span> },
    { title: '发票号', dataIndex: 'invoice_no', width: 150, render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span> },
    {
      title: '类型', dataIndex: 'invoice_type', width: 90,
      render: t => t === '专票' ? <Tag color="processing" style={{ borderRadius: 999 }}>专票</Tag> : <Tag style={{ borderRadius: 999 }}>{t}</Tag>,
    },
    { title: '价税合计', dataIndex: 'amount_total', width: 120, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
    { title: '税额', dataIndex: 'tax', width: 100, align: 'right', render: v => <span style={{ color: '#B8742A' }}>{fmtYuan(v)}</span> },
    { title: '归属主体', dataIndex: 'entity_name', width: 120, render: v => v || '—' },
    { title: '开票日期', dataIndex: 'invoice_date', width: 110, render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: s => s === '已开' ? <Tag color="success" style={{ borderRadius: 999 }}>已开</Tag> : (s === '作废' ? <Tag color="default" style={{ borderRadius: 999 }}>作废</Tag> : <Tag color="warning" style={{ borderRadius: 999 }}>待开</Tag>),
    },
    {
      title: '发票文件', dataIndex: 'file_url', width: 200,
      render: (v, r) => v ? (
        <Space size={4}>
          <a href={v} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: CORAL }}>
            <PaperClipOutlined /> {r.file_original_name || '查看发票'}
          </a>
          <Popconfirm title="删除该发票文件？" onConfirm={() => delFile(r.id)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : <span style={{ color: '#C0B8B0', fontSize: 12 }}>未上传</span>,
    },
    {
      title: '操作', width: 200, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Upload
            showUploadList={false}
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            customRequest={({ file, onSuccess, onError }) => {
              apiUpload('/api/sale-invoice/' + r.id + '/upload', file)
                .then(j => { if (j.success) { message.success('发票已上传'); loadSale(); loadTax(taxMonth); onSuccess(j); } else { message.error(j.error || '上传失败'); onError(new Error(j.error || '上传失败')); } })
                .catch(e => { message.error('上传失败：' + (e.message || '')); onError(e); });
            }}
          >
            <Button size="small" icon={<CloudUploadOutlined />}>传票</Button>
          </Upload>
          <Button size="small" onClick={() => { setEditSale(r); setSale({ customer_name: r.customer_name, customer_id: r.customer_id || '', tax_no: r.tax_no || '', order_nos: r.order_nos || '', invoice_no: r.invoice_no, invoice_type: r.invoice_type, amount_total: r.amount_total, tax: r.tax, entity_id: r.entity_id || '', invoice_date: r.invoice_date, status: r.status, remark: r.remark || '' }); setOpenSale(true); }}>编辑</Button>
          <Popconfirm title="删除该发票？" onConfirm={() => delSale(r.id)}><Button size="small" danger>删</Button></Popconfirm>
        </Space>
      ),
    },
  ];
  const saleTab = (
    <div>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card size="small" className="kpi-tap" style={{ borderRadius: 12 }}><Statistic title="本月开票数" value={invoices.filter(v => String(v.invoice_date || '').slice(0, 7) === dayjs().format('YYYY-MM')).length} suffix="张" /></Card></Col>
        <Col xs={12} md={6}><Card size="small" className="kpi-tap" style={{ borderRadius: 12 }}><Statistic title="专票销项税额" value={invoices.filter(v => v.invoice_type === '专票').reduce((s, v) => s + Number(v.tax || 0), 0)} precision={2} prefix="¥" /></Card></Col>
        <Col xs={12} md={6}><Card size="small" className="kpi-tap" style={{ borderRadius: 12 }}><Statistic title="累计开票额" value={invoices.reduce((s, v) => s + Number(v.amount_total || 0), 0)} precision={2} prefix="¥" /></Card></Col>
        <Col xs={12} md={6}><Card size="small" className="kpi-tap" style={{ borderRadius: 12 }}><Statistic title="累计销项税额" value={invoices.filter(v => v.invoice_type === '专票').reduce((s, v) => s + Number(v.tax || 0), 0)} precision={2} prefix="¥" /></Card></Col>
      </Row>
      <Space style={{ marginBottom: 12 }} wrap>
        <Upload showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.webp" beforeUpload={(file) => { recognizeAndSave(file); return false; }}>
          <Button type="primary" icon={<CloudUploadOutlined />} loading={recognizing}>上传发票自动入库</Button>
        </Upload>
        <Upload multiple showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.webp" beforeUpload={(file, fileList) => { recognizeBatch(fileList); return false; }}>
          <Button icon={<CloudUploadOutlined />} loading={batchLoading}>批量上传发票</Button>
        </Upload>
        <Button icon={<PlusOutlined />} onClick={() => { setEditSale(null); setSale({ customer_name: '', customer_id: '', tax_no: '', order_nos: '', invoice_no: '', invoice_type: '普票', amount_total: '', tax: '', entity_id: '', invoice_date: dayjs().format('YYYY-MM-DD'), status: '已开', remark: '' }); setOpenSale(true); }}>手动登记销项发票</Button>
        <Button icon={<ReloadOutlined />} onClick={loadSale}>刷新</Button>
        <span style={{ fontSize: 12, color: '#A08C82' }}>「上传发票自动入库」一步识别并保存；「批量上传」一次传多张（≤50）逐张识别入库，失败明细弹窗列出</span>
      </Space>
      <Table rowKey="id" size="small" dataSource={invoices} columns={saleColumns} pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="还没有销项发票，点「登记销项发票」记一条" /> }} />
    </div>
  );

  // ===== 税负测算 Tab =====
  const taxColumns = [
    { title: '主体', dataIndex: 'name', width: 140, render: (v, r) => <b>{v}</b> },
    {
      title: '纳税人身份', dataIndex: 'taxpayer_type', width: 130,
      render: (t, r) => (
        <Select size="small" value={t} style={{ width: 110 }} onChange={v => setTaxpayerType(r.entity_id, v)}
          options={[{ value: 'small', label: '小规模' }, { value: 'general', label: '一般纳税人' }]} />
      ),
    },
    { title: '销售收入', dataIndex: 'sales', width: 120, align: 'right', render: v => <span style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</span> },
    { title: '销项税额', dataIndex: 'out_tax', width: 110, align: 'right', render: v => <span style={{ color: '#B8742A' }}>{fmtYuan(v)}</span> },
    { title: '进项税额', dataIndex: 'in_tax', width: 110, align: 'right', render: v => <span style={{ color: '#3F8600' }}>{fmtYuan(v)}</span> },
    {
      title: '估算增值税', dataIndex: 'vat', width: 120, align: 'right',
      render: (v, r) => r.exempt ? <Tag color="success" style={{ borderRadius: 999 }}>免征</Tag> : <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', color: '#CF1322' }}>{fmtYuan(v)}</b>,
    },
    { title: '附加税', dataIndex: 'surcharge', width: 100, align: 'right', render: v => <span style={{ color: '#A08C82' }}>{fmtYuan(v)}</span> },
    { title: '合计', dataIndex: 'total', width: 110, align: 'right', render: v => <b style={{ fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(v)}</b> },
  ];
  const taxTab = (
    <div>
      <Card size="small" style={{ borderRadius: 14, border: '1px solid #F3E2D8', marginBottom: 12 }} styles={{ body: { padding: 16 } }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: '#5F5E5A' }}>测算月份</span>
          <Select style={{ width: 140 }} value={taxMonth} onChange={setTaxMonth}
            options={(() => { const out = []; const now = dayjs().startOf('month'); for (let i = 6; i >= -2; i--) { const m = now.subtract(i, 'month').format('YYYY-MM'); out.push({ value: m, label: m }); } return out; })()} />
          {taxData && (
            <span style={{ fontSize: 13, color: '#5F5E5A' }}>
              合计增值税 <b style={{ color: '#CF1322', fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(taxData.total_vat)}</b>
              ｜ 附加税 <b style={{ color: '#A08C82' }}>{fmtYuan(taxData.total_surcharge)}</b>
              ｜ 税负合计 <b style={{ color: INK, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{fmtYuan(taxData.total)}</b>
            </span>
          )}
        </Space>
        <Table rowKey="entity_id" size="small" dataSource={(taxData && taxData.rows) || []} columns={taxColumns} pagination={false}
          locale={{ emptyText: '暂无数据' }} />
        <div style={{ fontSize: 12, color: '#A08C82', marginTop: 10, lineHeight: 1.7 }}>
          口径：小规模纳税人季度 ≤30 万免征、超出按 1%；一般纳税人 = 销项 − 进项；附加税 = 增值税 × 12%（城建税 7% + 教育费附加 3% + 地方教育附加 2%）。
          销售收入的「主体归属」由账务流水 entity_id 决定（拼多多账单导入时按店铺映射），进项/销项税额分别来自「成本票」和「销项发票」登记的专票税额。此为估算方向，具体申报以税局为准。
        </div>
      </Card>
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: INK }}>
            <BankOutlined style={{ color: CORAL, marginRight: 8 }} />税务中心
          </div>
          <div style={{ fontSize: 12, color: '#A08C82', marginTop: 4 }}>
            销项发票 + 税负测算　|　销项 − 进项 = 应纳税额，与进项成本票、账务流水打通
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadSale}>刷新</Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'sale', label: <span><FileTextOutlined /> 销项开票</span>, children: tab === 'sale' ? saleTab : null },
          { key: 'tax', label: <span><CalculatorOutlined /> 税负测算</span>, children: tab === 'tax' ? taxTab : null },
        ]}
      />

      {/* 销项发票弹窗 */}
      <Modal title={editSale ? '编辑销项发票' : '登记销项发票'} open={openSale} onCancel={() => { setOpenSale(false); setEditSale(null); }} onOk={submitSale} okText="保存" cancelText="取消" destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div style={{ background: '#FFF7F2', border: '1px solid #FFE0CC', borderRadius: 8, padding: '10px 12px' }}>
            <Space wrap>
              <Upload showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.webp" beforeUpload={(file) => { recognizeInvoice(file); return false; }}>
                <Button icon={<CloudUploadOutlined />} loading={recognizing}>上传发票自动识别</Button>
              </Upload>
              <span style={{ fontSize: 12, color: '#A08C82' }}>上传电子发票 PDF 或图片，自动识别金额/税额/发票号/抬头/税率（含 1% 专票）</span>
            </Space>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>客户名称 <span style={{ color: '#CF1322' }}>*</span></div>
            <Input value={sale.customer_name} onChange={e => setSale({ ...sale, customer_name: e.target.value })} placeholder="开给谁（发票抬头）" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>关联客户档案（CRM）</div>
              <Select style={{ width: '100%' }} value={sale.customer_id || undefined} onChange={v => { const c = customers.find(x => Number(x.id) === Number(v)); setSale({ ...sale, customer_id: v || '', customer_name: c ? (sale.customer_name || c.name) : sale.customer_name }); }} placeholder="选 CRM 客户，自动带入抬头（可选）"
                options={customers.filter(c => c.name !== '待归属').map(c => ({ value: c.id, label: c.name }))} allowClear showSearch optionFilterProp="label" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>税号</div>
              <Input value={sale.tax_no} onChange={e => setSale({ ...sale, tax_no: e.target.value })} placeholder="纳税人识别号（可选）" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>关联订单</div>
            <Input value={sale.order_nos} onChange={e => setSale({ ...sale, order_nos: e.target.value })} placeholder="逗号分隔的订单号（可选）" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>发票类型</div>
              <Select style={{ width: '100%' }} value={sale.invoice_type} onChange={v => setSale({ ...sale, invoice_type: v })}
                options={saleTypes} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>状态</div>
              <Select style={{ width: '100%' }} value={sale.status} onChange={v => setSale({ ...sale, status: v })}
                options={[{ value: '已开', label: '已开' }, { value: '待开', label: '待开' }, { value: '作废', label: '作废' }]} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>发票号</div>
            <Input value={sale.invoice_no} onChange={e => setSale({ ...sale, invoice_no: e.target.value })} placeholder="发票号码（可选）" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>价税合计（元）<span style={{ color: '#CF1322' }}>*</span></div>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} value={sale.amount_total || null} onChange={v => setSale({ ...sale, amount_total: v })} placeholder="含税总额" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>税额（元，专票填）</div>
              <InputNumber style={{ width: '100%' }} min={0} precision={2} value={sale.tax || null} onChange={v => setSale({ ...sale, tax: v })} placeholder="销项税额" />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>归属主体</div>
            <Select style={{ width: '100%' }} value={sale.entity_id || undefined} onChange={v => setSale({ ...sale, entity_id: v })} placeholder="选择经营主体"
              options={entities.map(e => ({ value: e.id, label: e.name }))} allowClear />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>开票日期</div>
            <Input value={sale.invoice_date} onChange={e => setSale({ ...sale, invoice_date: e.target.value })} placeholder="YYYY-MM-DD" />
          </div>
          <div>
            <div style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 4 }}>备注</div>
            <Input value={sale.remark} onChange={e => setSale({ ...sale, remark: e.target.value })} placeholder="可选" />
          </div>
        </Space>
      </Modal>

      {/* 批量上传结果汇总弹窗 */}
      <Modal title="批量识别结果" open={batchOpen} onCancel={() => setBatchOpen(false)} footer={<Button type="primary" onClick={() => setBatchOpen(false)}>知道了</Button>} width={640}>
        {batchResult && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Statistic title="上传张数" value={batchResult.total} suffix="张" />
              <Statistic title="成功入库" value={batchResult.saved_count} suffix="张" valueStyle={{ color: '#3F8600' }} />
              <Statistic title="失败/跳过" value={batchResult.failed_count} suffix="张" valueStyle={{ color: '#CF1322' }} />
            </div>
            {batchResult.saved_count > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#3F8600' }}>✅ 成功入库</div>
                <Table rowKey={(r) => r.name + r.invoice_no} size="small" pagination={false}
                  dataSource={batchResult.saved}
                  columns={[
                    { title: '文件', dataIndex: 'name', ellipsis: true },
                    { title: '客户', dataIndex: 'customer_name', ellipsis: true },
                    { title: '发票号', dataIndex: 'invoice_no', width: 150, render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span> },
                    { title: '价税合计', dataIndex: 'amount_total', width: 100, align: 'right', render: v => <b>{fmtYuan(v)}</b> },
                    { title: '主体', dataIndex: 'entity_name', width: 140, render: v => v || '—' },
                  ]} />
              </div>
            )}
            {batchResult.failed_count > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#CF1322' }}>⚠️ 失败/跳过</div>
                <Table rowKey={(r) => r.name + r.reason} size="small" pagination={false}
                  dataSource={batchResult.failed}
                  columns={[
                    { title: '文件', dataIndex: 'name', ellipsis: true },
                    { title: '原因', dataIndex: 'reason' },
                  ]} />
                <div style={{ fontSize: 12, color: '#A08C82', marginTop: 6 }}>失败的多为识别不完整（缺客户名/金额）或发票号重复，可对这些单独用「上传发票自动入库」重试，识别不完整会自动回填表单补全。</div>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
}
